import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import MainLayoutWithoutFooter from '../components/MainLayoutWithoutFooter';
import StaffPinModal from '../components/staff/StaffPinModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import PrintersSection from '../components/offline/PrintersSection';
import { connection, useConnection } from '../offline/connection';
import { findWarning, pinNeedsRegistration, warningParts } from '../offline/state';
import { ageText, deviceView, freshnessRows, modeText, parkedLine, sourceText, staffText, uploadText } from '../offline/text';
import '../styles/offline.css';

const TONE = { ok: 'offOk', warn: 'offWarn', bad: 'offBad' };

const Row = ({ label, value, tone }) => (
  <div className="offRow">
    <span>{label}</span>
    <strong className={tone ? TONE[tone] : ''}>{value}</strong>
  </div>
);

/**
 * Offline mode (route /offline, from the header user menu): what the on-device offline engine has and needs. Connection and upload
 * counts, how fresh each kind of downloaded data is, whether staff PINs can work with no internet (device registration with an admin
 * PIN), parked orders for a manager (retry / discard), and the buttons "Refresh offline data" and "Retry upload". Without an engine
 * the page only says it is not available.
 */
const OfflineModePage = () => {
  const { state, device: deviceInfo, staff, engine, canForce } = useConnection();
  const [parked, setParked] = useState({ parked: [], flagged: [] });
  const [busy, setBusy] = useState('');
  const [registerFor, setRegisterFor] = useState(null); // 'register' | 'reissue' | null: the admin PIN prompt
  const [pinError, setPinError] = useState('');
  const [confirm, setConfirm] = useState(null); // { kind: 'discard' | 'unregister', uid?, title? }

  const reload = useCallback(async () => {
    if (!connection.hasEngine()) return;
    await connection.refresh();
    setParked(await connection.listParked());
  }, []);

  // read fresh on open, and again whenever the counts move (uploads finish, orders get parked)
  useEffect(() => {
    reload();
  }, [reload, state.outboxCount, state.parkedCount]);

  const run = async (name, fn, okMessage) => {
    setBusy(name);
    try {
      const r = await fn();
      if (r?.ok === false) toast.error(r.message || 'That did not work');
      else toast.success(okMessage || r?.message || 'Done');
    } finally {
      setBusy('');
      reload();
    }
  };

  const submitPin = async (pin) => {
    setBusy('pin');
    setPinError('');
    const r = await connection.call('POST', registerFor === 'reissue' ? 'reissue' : 'register', { pin });
    setBusy('');
    if (r?.ok) {
      setRegisterFor(null);
      toast.success(r.message || 'Device registered');
    } else {
      setPinError(r?.message || 'That did not work');
    }
    reload();
  };

  const device = deviceView(state, deviceInfo);
  const rows = freshnessRows(state.snapshotAgeSeconds);
  const warnings = (state.warnings || []).map(warningParts);
  const offline = state.mode !== 'online';
  const surcharges = findWarning(state, 'surcharges_not_downloaded');

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent offlinePage pb-2">
        <div className="container">
          <h2 className="lgTitle mb-3 pt-2">Offline mode</h2>

          {!engine ? (
            <div className="offCard">
              <p className="offNote mb-0">Offline mode is not available on this device.</p>
            </div>
          ) : (
            <>
              <section className="offCard">
                <h3>Connection</h3>
                <Row label="Status" value={modeText(state.mode)} tone={offline ? 'warn' : 'ok'} />
                <Row label="Orders" value={uploadText(state)} tone={state.parkedCount > 0 ? 'bad' : undefined} />
                {state.uploadNeedsLogin && <p className="offNote offBad">Sign in again to upload the orders taken offline.</p>}
                {state.lastUploadError && !state.uploadNeedsLogin && <p className="offNote">Last upload problem: {state.lastUploadError}</p>}
                {surcharges && <p className="offNote offWarn">{surcharges.text}</p>}
                <div className="offActions">
                  <button type="button" className="btn btnPrimary" onClick={() => run('refresh', () => connection.call('POST', 'refresh'), 'Offline data refreshed')} disabled={busy !== ''}>
                    {busy === 'refresh' ? 'Refreshing...' : 'Refresh offline data'}
                  </button>
                  <button type="button" className="btn btnPrimary" onClick={() => run('upload', () => connection.call('POST', 'retry-upload'))} disabled={busy !== ''}>
                    {busy === 'upload' ? 'Uploading...' : 'Retry upload'}
                  </button>
                  {canForce && (
                    <button type="button" className="btn btn-secondary" onClick={() => run('force', async () => ({ ok: await connection.setForced(state.mode !== 'forced_offline'), message: 'The offline switch did not change.' }), state.mode === 'forced_offline' ? 'Back online' : 'Offline mode is on')} disabled={busy !== ''}>
                      {state.mode === 'forced_offline' ? 'Go online' : 'Work offline'}
                    </button>
                  )}
                </div>
              </section>

              <section className="offCard">
                <h3>Staff PIN login</h3>
                <Row label="This device" value={device.label} tone={device.tone} />
                <Row label="PIN credentials" value={staffText(state, staff)} tone={state.capabilities.staffPin ? 'ok' : pinNeedsRegistration(state) ? 'bad' : undefined} />
                {staff.source && <Row label="Credentials" value={sourceText(staff.source)} />}
                {deviceInfo.name && <Row label="Registered as" value={deviceInfo.name} />}
                <p className="offNote">{device.help}</p>
                <div className="offActions">
                  {device.action === 'register' && (
                    <button type="button" className="btn btnPrimary" onClick={() => { setPinError(''); setRegisterFor('register'); }} disabled={busy !== '' || offline}>
                      {device.status === 'revoked' ? 'Register again' : 'Register this device'}
                    </button>
                  )}
                  {device.action === 'reissue' && (
                    <button type="button" className="btn btnPrimary" onClick={() => { setPinError(''); setRegisterFor('reissue'); }} disabled={busy !== '' || offline}>
                      Renew with an admin PIN
                    </button>
                  )}
                  {device.status === 'active' && (
                    <button type="button" className="btn btn-secondary" onClick={() => setConfirm({ kind: 'unregister' })} disabled={busy !== ''}>
                      Unregister
                    </button>
                  )}
                </div>
                {offline && device.action && <p className="offNote">Go online to register this device.</p>}
              </section>

              {parked.parked.length > 0 && (
                <section className="offCard">
                  <h3>Orders that need attention</h3>
                  <p className="offNote">The server did not accept these. They stay on this device. Retry sends them again; discard gives up on them.</p>
                  <ul className="offList">
                    {parked.parked.map((p) => {
                      const line = parkedLine(p);
                      return (
                        <li key={p.uid}>
                          <div>
                            <strong>{line.title}</strong>
                            <small>{line.detail}</small>
                            {line.code && <small className="offCode">{line.code}</small>}
                          </div>
                          <div className="offActions mt-0">
                            <button type="button" className="btn btnPrimary" onClick={() => run(`retry-${p.uid}`, () => connection.call('POST', 'retry', { uid: p.uid }), 'Queued again')} disabled={busy !== ''}>
                              Retry
                            </button>
                            <button type="button" className="btn btn-danger" onClick={() => setConfirm({ kind: 'discard', uid: p.uid, title: line.title })} disabled={busy !== ''}>
                              Discard
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              <PrintersSection offline={offline} />

              {warnings.length > 0 && (
                <section className="offCard">
                  <h3>Warnings</h3>
                  <ul className="offList">
                    {warnings.map((w, i) => (
                      <li key={`${w.key}${i}`}>
                        <small>{w.text}</small>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="offCard">
                <h3>Downloaded data</h3>
                {rows.filter((r) => r.main).map((r) => (
                  <Row key={r.key} label={r.label} value={r.text} tone={r.tone} />
                ))}
                <details>
                  <summary>More data</summary>
                  {rows.filter((r) => !r.main).map((r) => (
                    <Row key={r.key} label={r.label} value={r.text} tone={r.tone} />
                  ))}
                </details>
                {deviceInfo.lastStaffRefreshAt ? <p className="offNote">Staff PINs last checked {ageText(Math.floor((Date.now() - deviceInfo.lastStaffRefreshAt) / 1000))}.</p> : null}
              </section>
            </>
          )}
        </div>
      </div>

      <StaffPinModal
        isOpen={registerFor !== null}
        onCancel={() => (busy === '' ? setRegisterFor(null) : undefined)}
        onConfirm={submitPin}
        title={registerFor === 'reissue' ? 'Renew this device' : 'Register this device'}
        subtitle="Enter an admin PIN. The server then gives this device its own offline credential."
        error={pinError}
        busy={busy === 'pin'}
      />

      <ConfirmModal
        isOpen={confirm?.kind === 'discard'}
        title="Discard this order?"
        message={`${confirm?.title || 'This record'} will not be uploaded. It stays on this device but the server will never get it. Retry instead if you are not sure.`}
        confirmText="Discard"
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const uid = confirm.uid;
          setConfirm(null);
          await run('discard', () => connection.call('POST', 'discard', { uid }), 'Discarded');
        }}
      />
      <ConfirmModal
        isOpen={confirm?.kind === 'unregister'}
        title="Unregister this device?"
        message="Staff PINs will stop working offline here until it is registered again. Orders and uploads are not affected."
        confirmText="Unregister"
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          setConfirm(null);
          await run('unregister', () => connection.call('POST', 'unregister'), 'Device unregistered');
        }}
      />
    </MainLayoutWithoutFooter>
  );
};

export default OfflineModePage;
